import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ConsentContext, ConsentContextType } from './ConsentContext';
import { useBrand } from './BrandContext';
import { authService } from '../pages/Auth/authService';
import { ApiClient } from '../utils/apiClient';
import { forgetAttribution, persistAttribution } from '../utils/marketing/attribution';
import {
  ConsentDecision,
  isValidPixelId,
  readConsent,
  writeConsent,
} from '../utils/marketing/consent';
import { deletePixelCookies, loadPixel, revokeConsent } from '../utils/marketing/metaPixel';
import { isTrackingScope } from '../utils/marketing/scope';

interface ConsentProviderProps {
  children: ReactNode;
}

export const ConsentProvider: React.FC<ConsentProviderProps> = ({ children }) => {
  const { brand, isLoading: isBrandLoading } = useBrand();
  const { pathname } = useLocation();
  const [decision, setDecision] = useState<ConsentDecision | null>(() => readConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pixelLoaded, setPixelLoaded] = useState(false);

  const trackingScope = isTrackingScope(brand, isBrandLoading, pathname);
  const pixelId = isValidPixelId(brand.meta_pixel_id) ? brand.meta_pixel_id : null;
  const bannerEnabled = trackingScope && pixelId !== null;
  const marketing = decision?.marketing === true;

  useEffect(() => {
    if (bannerEnabled && marketing && pixelId) {
      loadPixel(pixelId);
      setPixelLoaded(true);
    }
  }, [bannerEnabled, marketing, pixelId]);

  const decide = useCallback(
    (nextMarketing: boolean) => {
      const withdrawn = decision?.marketing === true && !nextMarketing;
      const next = writeConsent(nextMarketing);
      setDecision(next);
      setSettingsOpen(false);

      if (nextMarketing) {
        persistAttribution();
      } else {
        forgetAttribution();
      }
      if (withdrawn) {
        revokeConsent();
        deletePixelCookies();
        setPixelLoaded(false);
      }

      // The server checks the consent again before it sends an event. During
      // the registration the user has tokens but no auth context yet.
      if (authService.getAccessToken()) {
        ApiClient.put('/marketing/consent/', {
          consent: nextMarketing,
          consent_version: next.version,
        }).catch(() => undefined);
      }
    },
    [decision]
  );

  const dismiss = useCallback(() => {
    if (decision) {
      setSettingsOpen(false);
    } else {
      decide(false);
    }
  }, [decision, decide]);

  const value = useMemo<ConsentContextType>(
    () => ({
      trackingScope,
      bannerEnabled,
      bannerVisible: bannerEnabled && (decision === null || settingsOpen),
      settingsOpen,
      marketing,
      marketingActive: bannerEnabled && marketing && pixelLoaded,
      accept: () => decide(true),
      decline: () => decide(false),
      saveSettings: decide,
      openSettings: () => setSettingsOpen(true),
      dismiss,
    }),
    [trackingScope, bannerEnabled, decision, settingsOpen, marketing, pixelLoaded, decide, dismiss]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};
